<template>
  <MacroNestedPopover
    :disabled="disabledMenu"
    @open-change="handleOpenChange"
    @menu-click="(type) => emit('menu-click', type)"
    @sub-action-click="(type) => emit('sub-action-click', type)"
  >
    <div :class="getClass()">
      <template v-if="record.type === 1 || record.type === 3">
        <div
          :class="`flex ${visualMacro === 'text-down' ? 'flex-col-reverse' : 'flex-col'} justify-center items-center text-s-medium w-full h-full`"
        >
          <template v-if="mouseActionIcon">
            <Icon
              v-if="record.type === 1 && visualMacro === 'text-up'"
              icon="Direction-FIll-Up1"
              class="w-3.5 h-3.5"
            />
            <Icon
              v-if="record.type === 1 && visualMacro === 'text-down'"
              icon="Direction-FIll-Down1"
              class="w-3.5 h-3.5"
            />
            <Icon :icon="mouseActionIcon" :class="record.type === 3 ? 'size-8' : 'size-7'" />
          </template>
          <template v-else>
            <Icon v-if="visualMacro === 'text-up'" icon="Direction-FIll-Up1" class="w-3.5 h-3.5" />
            <Icon v-else icon="Direction-FIll-Down1" class="w-3.5 h-3.5" />
            <div class="text-m-medium cursor-default truncate max-w-full">
              {{ actionText }}
            </div>
          </template>
        </div>
      </template>

      <template v-else-if="record.type === 4">
        <div
          class="flex flex-col justify-center items-center text-m-medium w-full h-full cursor-default"
        >
          <template v-if="editingDelay">
            <input
              ref="delayInputRef"
              v-model="delayInput"
              type="text"
              inputmode="numeric"
              class="w-10 h-4 rounded text-black bg-white text-center outline-none"
              @input="validateDelayInput"
              @blur="saveDelay"
              @keyup.enter="saveDelay"
              @keyup.esc="cancelDelay"
            />
          </template>
          <template v-else>
            <div class="h-4.5" @dblclick.stop="startEditDelay">{{ displayTime.value }}</div>
          </template>
          <div class="h-4.5 mb-1">{{ displayTime.unit }}</div>
        </div>
      </template>

      <template v-else>
        <div
          class="flex flex-col justify-center items-center text-m-medium w-full h-full cursor-default gap-0.5"
        >
          <div class="h-4 flex items-center w-full min-w-0">
            <span class="shrink-0 font-[Barlow]">X:</span>
            <template v-if="editingX">
              <input
                ref="xInputRef"
                v-model="xInput"
                type="text"
                inputmode="numeric"
                class="w-0 min-w-0 flex-1 h-4 px-0.5 font-[Barlow] tabular-nums text-black rounded bg-white text-center outline-none"
                @input="validateCoordinateInput('x')"
                @blur="saveCoordinate('x')"
                @keyup.enter="saveXAndEditY"
                @keyup.esc="cancelCoordinate('x')"
              />
            </template>
            <span
              v-else
              class="w-0 min-w-0 flex-1 text-center font-[Barlow] tabular-nums"
              @dblclick.stop="startEditX"
              >{{ (record as MacroEditorCoordinateAction).x_coordinate }}</span
            >
          </div>
          <div class="h-4 flex items-center w-full min-w-0">
            <span class="shrink-0 font-[Barlow]">Y:</span>
            <template v-if="editingY">
              <input
                ref="yInputRef"
                v-model="yInput"
                type="text"
                inputmode="numeric"
                class="w-0 min-w-0 flex-1 h-4 px-0.5 font-[Barlow] tabular-nums text-black rounded bg-white text-center outline-none"
                @input="validateCoordinateInput('y')"
                @blur="saveCoordinate('y')"
                @keyup.enter="saveCoordinate('y')"
                @keyup.esc="cancelCoordinate('y')"
              />
            </template>
            <span
              v-else
              class="w-0 min-w-0 flex-1 text-center font-[Barlow] tabular-nums"
              @dblclick.stop="startEditY"
              >{{ (record as MacroEditorCoordinateAction).y_coordinate }}</span
            >
          </div>
        </div>
      </template>
    </div>
  </MacroNestedPopover>
</template>

<script setup lang="ts">
  import { Icon } from '@rapoo/element'
  import { computed, nextTick, ref } from 'vue'

  import MacroNestedPopover, { MainActionType, SubActionType } from './MacroNestedPopover.vue'
  import type {
    MacroEditorCoordinateAction,
    MacroEditorDelayAction,
    MacroEditorKeyAction,
    MacroEditorWheelAction,
  } from '../../types'
  import { MACRO_DELAY_MAX, MACRO_DELAY_MIN, MOUSE_KEY_CODE } from '../../types'
  import type { DraftRecordItem } from '../../composables/useDraftDataManager'
  import { getMacroKeyLabelByCode, getWheelDirectionLabel } from '../../utils/keycodeMaps'

  type MacroStatus = 'default' | 'hover' | 'active' | 'new'

  const DELAY_INPUT_MIN = 0
  const DELAY_INPUT_MAX = 99999

  const mouseEventIcon = {
    left: 'VirtualKey-Wireframe-Left',
    right: 'VirtualKey-Wireframe-Right',
    middle: 'VirtualKey-Wireframe-Middle',
    back: 'VirtualKey-Wireframe-Back',
    forward: 'VirtualKey-Wireframe-Forward',
    rollback: 'VirtualKey-Wireframe-Rollerback',
    rollforward: 'VirtualKey-Wireframe-Rollerforward',
    rollleft: 'VirtualKey-Wireframe-Rollerleft',
    rollright: 'VirtualKey-Wireframe-Rollerright',
  }

  const mouseKeyCodeToIconKey: Partial<Record<number, keyof typeof mouseEventIcon>> = {
    [MOUSE_KEY_CODE.left]: 'left',
    [MOUSE_KEY_CODE.right]: 'right',
    [MOUSE_KEY_CODE.middle]: 'middle',
    [MOUSE_KEY_CODE.back]: 'back',
    [MOUSE_KEY_CODE.forward]: 'forward',
  }

  const props = withDefaults(
    defineProps<{
      record: DraftRecordItem
      status?: MacroStatus
      disabledMenu?: boolean
      coordinateMin?: number
      coordinateMax?: number
    }>(),
    {
      status: 'default',
      disabledMenu: false,
      coordinateMin: -2048,
      coordinateMax: 2047,
    },
  )

  const emit = defineEmits<{
    (e: 'menu-click', type: MainActionType): void
    (e: 'sub-action-click', type: SubActionType): void
    (e: 'update:delay', time: number): void
    (e: 'update:coordinate', payload: { x_coordinate?: number; y_coordinate?: number }): void
  }>()

  const menuOpen = ref(false)

  const handleOpenChange = (open: boolean) => {
    menuOpen.value = open
  }

  const visualStatus = computed<MacroStatus>(() => {
    if (menuOpen.value) return 'active'

    if (props.status !== 'default') return props.status

    if (props.record.__status === 'added' || props.record.__status === 'modified') {
      return 'new'
    }

    return 'default'
  })

  const visualMacro = computed<'text-up' | 'text-down'>(() => {
    if (props.record.type === 1) {
      return (props.record as MacroEditorKeyAction).action === 0 ? 'text-up' : 'text-down'
    }

    // if (props.record.type === 3) {
    //   const direction = (props.record as MacroEditorWheelAction).direction
    //   return direction === 'backward' ? 'text-down' : 'text-up'
    // }

    return 'text-up'
  })

  const actionText = computed(() => {
    if (props.record.__placeholder) return ''

    if (props.record.type === 1) {
      return getMacroKeyLabelByCode((props.record as MacroEditorKeyAction).keystroke)
    }

    if (props.record.type === 3) {
      return getWheelDirectionLabel({
        vWheel: (props.record as MacroEditorWheelAction).vWheel,
        hWheel: (props.record as MacroEditorWheelAction).hWheel,
      })
    }

    return ''
  })

  const mouseActionIcon = computed(() => {
    if (props.record.type === 1) {
      const keyCode = (props.record as MacroEditorKeyAction).keystroke
      const iconKey = mouseKeyCodeToIconKey[keyCode]
      return iconKey ? mouseEventIcon[iconKey] : ''
    }

    if (props.record.type === 3) {
      const direction = props.record as MacroEditorWheelAction
      if (direction.hWheel !== 0) {
        return direction.hWheel === -1 ? mouseEventIcon['rollright'] : mouseEventIcon['rollleft']
      } else {
        return direction.vWheel === -1 ? mouseEventIcon['rollback'] : mouseEventIcon['rollforward']
      }
    }

    return ''
  })

  function truncateToFixed(value: number, decimals: number): number {
    const factor = Math.pow(10, decimals)
    return Math.trunc(value * factor) / factor
  }

  const displayTime = computed(() => {
    if (props.record.type !== 4) return { value: 0, unit: '-' }
    if (editingDelay.value) {
      return { value: (props.record as MacroEditorDelayAction).time, unit: 'ms' }
    }

    const time = (props.record as MacroEditorDelayAction).time

    if (time < 1000) {
      return { value: time, unit: 'ms' }
    }
    if (time < 60000) {
      return { value: truncateToFixed(time / 1000, 2), unit: 's' }
    }
    return { value: truncateToFixed(time / 1000 / 60, 2), unit: 'min' }
  })

  const getClass = () => {
    const basicClass = 'size-full py-0.5 box-border rounded-lg text-m-medium'

    switch (visualStatus.value) {
      case 'hover':
        return `${basicClass} bg-key text-text-glass-hover`
      case 'active':
        return `${basicClass} bg-white text-black`
      case 'new':
        return `${basicClass} bg-blue-light-default text-black`
      default:
        return `${basicClass} bg-key text-black-white`
    }
  }

  const delayInputRef = ref<HTMLInputElement>()
  const xInputRef = ref<HTMLInputElement>()
  const yInputRef = ref<HTMLInputElement>()

  const editingDelay = ref(false)
  const delayInput = ref('0')
  const editingX = ref(false)
  const editingY = ref(false)
  const xInput = ref('0')
  const yInput = ref('0')

  const clamp = (value: number, min: number, max: number) => {
    return Math.min(max, Math.max(min, value))
  }

  /** 延迟：仅非负整数；坐标：可选前导负号 + 整数 */
  const sanitizeIntegerInput = (raw: string, allowNegative: boolean) => {
    const cleaned = raw.replace(/[^\d-]/g, '')
    if (!allowNegative) return cleaned.replace(/-/g, '')

    const negative = cleaned.startsWith('-')
    const digits = cleaned.replace(/-/g, '')
    return negative ? `-${digits}` : digits
  }

  const startEditDelay = async () => {
    if (props.record.type !== 4) return

    editingDelay.value = true
    delayInput.value = `${(props.record as MacroEditorDelayAction).time}`

    await nextTick()
    delayInputRef.value?.focus()
    delayInputRef.value?.select()
  }

  const validateDelayInput = () => {
    const sanitized = sanitizeIntegerInput(delayInput.value, false)
    if (sanitized === '') {
      delayInput.value = ''
      return
    }

    const parsed = Number.parseInt(sanitized, 10)
    if (Number.isNaN(parsed)) {
      delayInput.value = ''
      return
    }

    delayInput.value = String(clamp(parsed, DELAY_INPUT_MIN, DELAY_INPUT_MAX))
  }

  const saveDelay = () => {
    if (props.record.type !== 4) return

    const parsed = Number.parseInt(delayInput.value, 10)
    if (Number.isNaN(parsed)) {
      editingDelay.value = false
      return
    }

    emit('update:delay', clamp(parsed, MACRO_DELAY_MIN, MACRO_DELAY_MAX))
    editingDelay.value = false
  }

  const cancelDelay = () => {
    editingDelay.value = false
  }

  const startEditX = async () => {
    if (props.record.type !== 2) return

    editingX.value = true
    xInput.value = `${(props.record as MacroEditorCoordinateAction).x_coordinate}`

    await nextTick()
    xInputRef.value?.focus()
    xInputRef.value?.select()
  }

  const startEditY = async () => {
    if (props.record.type !== 2) return

    editingY.value = true
    yInput.value = `${(props.record as MacroEditorCoordinateAction).y_coordinate}`

    await nextTick()
    yInputRef.value?.focus()
    yInputRef.value?.select()
  }

  const validateCoordinateInput = (axis: 'x' | 'y') => {
    const source = axis === 'x' ? xInput : yInput
    const sanitized = sanitizeIntegerInput(source.value, true)

    // 允许编辑中的单独 "-"，便于输入负数
    if (sanitized === '' || sanitized === '-') {
      source.value = sanitized
      return
    }

    const parsed = Number.parseInt(sanitized, 10)
    if (Number.isNaN(parsed)) {
      source.value = ''
      return
    }

    source.value = String(clamp(parsed, props.coordinateMin, props.coordinateMax))
  }

  const saveCoordinate = (axis: 'x' | 'y') => {
    if (props.record.type !== 2) return

    const current = props.record as MacroEditorCoordinateAction
    const source = axis === 'x' ? xInput.value : yInput.value
    const parsed = Number.parseInt(source, 10)

    if (Number.isNaN(parsed)) {
      editingX.value = false
      editingY.value = false
      return
    }

    const clamped = clamp(parsed, props.coordinateMin, props.coordinateMax)
    emit('update:coordinate', {
      x_coordinate: axis === 'x' ? clamped : current.x_coordinate,
      y_coordinate: axis === 'y' ? clamped : current.y_coordinate,
    })

    if (axis === 'x') editingX.value = false
    if (axis === 'y') editingY.value = false
  }

  const saveXAndEditY = async () => {
    saveCoordinate('x')
    await nextTick()
    startEditY()
  }

  const cancelCoordinate = (axis: 'x' | 'y') => {
    if (axis === 'x') editingX.value = false
    if (axis === 'y') editingY.value = false
  }

  defineExpose({
    startEditDelay,
    startEditX,
  })
</script>

<style scoped></style>
